import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, Twitter, Youtube, MessageCircle } from 'lucide-react';
import { serviceAreas } from '../data/mock';

const Footer = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-gray-950 text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-10 sm:py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
          {/* Company Info */}
          <div>
            <div className="mb-6">
              <img 
                src="/logo.png" 
                alt="RO Dekho - Purification Ka Expert" 
                className="h-14 sm:h-16 w-auto"
                data-testid="footer-logo"
              />
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Jaipur&apos;s most trusted RO water purifier installation, repair, and maintenance service provider. 
              Expert solutions for pure, healthy drinking water.
            </p>
            <p className="text-xs text-gray-500 mb-4">
              A venture of Arpsan Lifestyle Technology Pvt Ltd
            </p>
            <div className="flex gap-3">
              <a href="#" className="bg-white/10 hover:bg-blue-600 p-2.5 rounded-lg transition-colors">
                <Facebook size={20} />
              </a>
              <a href="#" className="bg-white/10 hover:bg-pink-600 p-2.5 rounded-lg transition-colors">
                <Instagram size={20} />
              </a>
              <a href="#" className="bg-white/10 hover:bg-blue-400 p-2.5 rounded-lg transition-colors">
                <Twitter size={20} />
              </a>
              <a href="#" className="bg-white/10 hover:bg-red-600 p-2.5 rounded-lg transition-colors">
                <Youtube size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-white">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <button onClick={() => scrollToSection('home')} className="text-gray-400 hover:text-lime-400 transition-colors">
                  Home
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('services')} className="text-gray-400 hover:text-lime-400 transition-colors">
                  Services
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('products')} className="text-gray-400 hover:text-lime-400 transition-colors">
                  Products
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('why-us')} className="text-gray-400 hover:text-lime-400 transition-colors">
                  Why Choose Us
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('testimonials')} className="text-gray-400 hover:text-lime-400 transition-colors">
                  Testimonials
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-lime-400 transition-colors">
                  Contact Us
                </button>
              </li>
              <li>
                <Link to="/track/order" className="text-gray-400 hover:text-lime-400 transition-colors">
                  Track Order
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-white">Our Services</h3>
            <ul className="space-y-3">
              <li className="text-gray-400">RO Installation</li>
              <li className="text-gray-400">RO Repair & Service</li>
              <li className="text-gray-400">AMC Packages</li>
              <li className="text-gray-400">Filter Replacement</li>
              <li className="text-gray-400">Water Testing</li>
              <li className="text-gray-400">Emergency Service</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-white">Contact Us</h3>
            <ul className="space-y-4">
              <li>
                <a href="tel:+919887068888" className="flex items-center gap-3 text-gray-400 hover:text-lime-400 transition-colors">
                  <Phone size={18} />
                  <span>+91 9887068888</span>
                </a>
              </li>
              <li>
                <a href="https://wa.me/919887068888" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-gray-400 hover:text-lime-400 transition-colors">
                  <MessageCircle size={18} />
                  <span>WhatsApp: +91 9887068888</span>
                </a>
              </li>
              <li>
                <a href="mailto:support@rodekho.com" className="flex items-center gap-3 text-gray-400 hover:text-lime-400 transition-colors">
                  <Mail size={18} />
                  <span>support@rodekho.com</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-gray-400">
                <MapPin size={18} className="mt-1" />
                <span>Main Tonk Road, Sector 3, Partap Nagar, Sanganer, Jaipur, Rajasthan</span>
              </li>
            </ul>
            <div className="mt-6 pt-6 border-t border-gray-800">
              <div className="text-sm font-semibold text-white mb-2">Business Hours</div>
              <div className="text-gray-400 text-sm">
                <div>Mon - Sat: 8:00 AM - 8:00 PM</div>
                <div>Sunday: 9:00 AM - 6:00 PM</div>
                <div className="text-lime-400 font-semibold mt-1">24/7 Emergency Service</div>
              </div>
            </div>
          </div>
        </div>

        {/* Service Areas */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="text-lg font-bold text-white">Service Areas in Jaipur</h3>
            <Link to="/service-areas" className="text-sm text-lime-400 hover:text-lime-300 font-semibold" data-testid="footer-view-all-areas">
              View all areas →
            </Link>
          </div>
          <div className="flex flex-wrap gap-3">
            {serviceAreas.map((area, index) => {
              const slug = 'jaipur-' + area.toLowerCase().replace(/\s+/g, '-');
              return (
                <Link
                  key={index}
                  to={`/city/${slug}`}
                  data-testid={`footer-area-${slug}`}
                  className="bg-white/5 hover:bg-lime-500 text-gray-300 hover:text-gray-900 px-4 py-2 rounded-full text-sm font-medium transition-colors"
                >
                  {area}
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <div className="text-center md:text-left">
              © {new Date().getFullYear()} <span className="text-white font-semibold">Arpsan Lifestyle Technology Pvt Ltd</span>. All rights reserved.
              <br className="md:hidden" />
              <span className="hidden md:inline"> | </span>
              Brand: <a href="https://www.rodekho.in" className="text-lime-400 hover:text-lime-300 transition-colors">
                RO Dekho
              </a>
            </div>
            <div className="flex gap-6">
              <Link to="/terms-and-conditions" className="hover:text-lime-400 transition-colors">Terms &amp; Conditions</Link>
              <button className="hover:text-lime-400 transition-colors">Privacy Policy</button>
              <button className="hover:text-lime-400 transition-colors">Refund Policy</button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
